import { Button, Spinner } from '@1trading/react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountHolderCell } from '@/components/data-table/cells';
import { FilterChips } from '@/components/data-table/column-filter-bar';
import { ColumnFilterHeader } from '@/components/data-table/column-filter-header';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { reportCsvQueryOptions } from '@/hooks/queries/reports/use-report-csv';
import { useRequestAccountCleanup } from '@/hooks/queries/users/use-account-cleanup';
import { type ReportReadyPush, useAsyncReport } from '@/hooks/use-async-report';
import { type ColumnFilterConfig, useColumnFilters } from '@/hooks/use-column-filters';
import { ACCOUNT_CLEANUP_REPORT_TYPE } from '@/services/api/account-cleanup';
import { uniqueSorted } from '@/utils/array';
import { buildCleanupRequest, type CleanupRow, isApprovable, parseCleanupRows } from './cleanup-report';
import { ProcessCleanupDialog } from './process-cleanup-dialog';

const PAGE_SIZE = 50;
const VIEW_PATHNAME = '/operations/user-audit/cleanup/view';

const TextCell = ({ value }: { value: string }) => (value ? <span className="whitespace-nowrap">{value}</span> : <span className="text-muted-foreground">—</span>);
TextCell.displayName = 'TextCell';

interface ColumnSpec {
  field: keyof CleanupRow & string;
  title: string;
  filter: ColumnFilterConfig<CleanupRow>['filter'];
}

const COLUMN_SPECS: ColumnSpec[] = [
  { field: 'pid', title: 'Account', filter: 'text' },
  { field: 'createdDate', title: 'Created', filter: 'date' },
  { field: 'lastLogin', title: 'Last login', filter: 'date' },
  { field: 'loginsCount', title: 'Logins', filter: 'text' },
  { field: 'status', title: 'Status', filter: 'choice' },
  { field: 'authRecordsCount', title: 'Auth rows', filter: 'text' },
  { field: 'fastCustomerDetailsRecordsCount', title: 'Customer rows', filter: 'text' },
  { field: 'piiVaultFileCount', title: 'PII files', filter: 'text' },
  { field: 'sumsubVerdict', title: 'Sumsub verdict', filter: 'choice' },
];

const FILTER_CONFIGS: ColumnFilterConfig<CleanupRow>[] = COLUMN_SPECS.map((spec) => ({
  field: spec.field,
  title: spec.title,
  filter: spec.filter,
  getText: (row) => String(row[spec.field]),
}));

const CHOICE_FIELDS = COLUMN_SPECS.filter((spec) => spec.filter === 'choice').map((spec) => spec.field);

export interface AccountCleanupReviewProps {
  reportKey: string;
}

/**
 * Review screen for one cleanup report: filter its candidates, select the accounts to close, run. The
 * worker re-checks each account and skips any that no longer qualify, so there is no rehearsal step.
 * The run outcome arrives over the websocket, so the async report hook lives here rather than in the dialog.
 */
export const AccountCleanupReview = ({ reportKey }: AccountCleanupReviewProps) => {
  const navigate = useNavigate();
  const requestMutation = useRequestAccountCleanup();

  const [selectedRows, setSelectedRows] = useState<CleanupRow[]>([]);
  // Bumping remounts the DataTable to clear its internal row selection
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Set from the confirm dialog, where a rejection stays visible; the async report hook only waits behind it
  const acceptedRequestRef = useRef<Promise<unknown>>(Promise.resolve());

  const csvQuery = useQuery(reportCsvQueryOptions(reportKey));
  const rows = useMemo(() => (csvQuery.data ? parseCleanupRows(csvQuery.data) : []), [csvQuery.data]);
  const candidates = useMemo(() => rows.filter(isApprovable), [rows]);
  const excludedCount = rows.length - candidates.length;
  // Blanks are left out: the filter hook offers its own (Blanks) entry
  const optionsByField = useMemo(
    () => Object.fromEntries(CHOICE_FIELDS.map((field) => [field, uniqueSorted(candidates.map((row) => String(row[field])).filter(Boolean))])),
    [candidates],
  );

  const filterState = useColumnFilters(candidates, FILTER_CONFIGS);
  const filteredRows = filterState.filteredData;

  const openOutcome = useCallback((message: ReportReadyPush) => navigate({ pathname: VIEW_PATHNAME, search: `?key=${encodeURIComponent(message.key)}` }), [navigate]);
  const { isGenerating, requestReport } = useAsyncReport({
    reportType: ACCOUNT_CLEANUP_REPORT_TYPE,
    label: 'Account cleanup run',
    request: () => acceptedRequestRef.current,
    onReady: openOutcome,
  });

  const selectedIds = selectedRows.map((row) => row.pid);

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
    setSelectionResetKey((key) => key + 1);
  }, []);

  const startRun = async (justification: string) => {
    // Await acceptance here so a 400/403 surfaces in the confirm dialog; the hook then waits for the outcome push
    acceptedRequestRef.current = requestMutation.mutateAsync(buildCleanupRequest(reportKey, selectedIds, justification, false));
    await acceptedRequestRef.current;
    void requestReport();
    clearSelection();
  };

  // Not memoised: filterState is a fresh object every render, so the header filter controls would defeat any memo
  const columns = COLUMN_SPECS.map((spec, index): ColumnDef<CleanupRow, unknown> => {
    const config = FILTER_CONFIGS[index];
    return {
      id: spec.field,
      header: spec.title,
      accessorFn: (row) => row[spec.field],
      meta: config ? { filterControl: <ColumnFilterHeader config={config} options={optionsByField[spec.field]} state={filterState} /> } : undefined,
      cell: ({ row }) => {
        const value = String(row.original[spec.field]);
        return spec.field === 'pid' ? <AccountHolderCell value={value || undefined} /> : <TextCell value={value} />;
      },
    };
  });

  if (csvQuery.isPending) {
    return (
      <div className="flex items-center justify-center gap-3 py-16 text-muted-foreground">
        <Spinner className="size-5" />
        <span className="text-sm">Loading report…</span>
      </div>
    );
  }

  if (csvQuery.isError) {
    return <QueryErrorBanner error={csvQuery.error} onRetry={() => csvQuery.refetch()} title="Failed to load the report file" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm tabular-nums">
          {filteredRows?.length ?? 0} of {candidates.length} candidate(s)
          {excludedCount > 0 ? `, ${excludedCount} excluded row(s) hidden` : ''}
        </p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{selectedIds.length} selected</span>
          <Button className="gap-2" disabled={selectedIds.length === 0 || isGenerating} onClick={() => setIsDialogOpen(true)} type="button" variant="destructive">
            {isGenerating ? <Spinner className="size-4" /> : <Trash2 className="size-4" />}
            Delete selected
          </Button>
        </div>
      </div>
      <FilterChips clearAll={filterState.clearAll} configs={FILTER_CONFIGS} filters={filterState.filters} removeFilter={filterState.removeFilter} />
      <DataTable
        columns={columns}
        data={filteredRows}
        emptyMessage="No candidates match the current filters."
        enableRowSelection
        getRowId={(row) => row.pid}
        key={selectionResetKey}
        onSelectionChange={setSelectedRows}
        pageSize={PAGE_SIZE}
        stableColumnWidths
        surface={false}
      />
      {isDialogOpen ? <ProcessCleanupDialog accountHolderIds={selectedIds} onClose={() => setIsDialogOpen(false)} onConfirm={startRun} reportKey={reportKey} /> : null}
    </div>
  );
};
AccountCleanupReview.displayName = 'AccountCleanupReview';
